/**
 * UTM / referral attribution capture.
 *
 * Strategy: capture marketing URL params on the FIRST landing in this
 * browser session, persist them in localStorage for 30 days, and stamp
 * the values onto the cart's metadata when the cart is created. The
 * earliest-touch wins so a user who lands via Google Ads → browses →
 * comes back via direct gets credited to the Google Ads click.
 *
 * Subsequent paid clicks (different gclid / fbclid) overwrite, so the
 * most recent paid click attribution prevails over an old organic click.
 * This matches how GA4's data-driven attribution treats the chain.
 */
const STORAGE_KEY = "plusbake.attribution"
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

const PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
  "ttclid",
] as const

type ParamKey = (typeof PARAM_KEYS)[number]

export type AttributionPayload = Partial<Record<ParamKey, string>> & {
  landing_url?: string
  referrer?: string
  captured_at?: string
}

const isPaidClickIdPresent = (params: AttributionPayload): boolean =>
  Boolean(params.gclid || params.fbclid || params.msclkid || params.ttclid)

const readStored = (): AttributionPayload | null => {
  if (typeof window === "undefined") return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AttributionPayload & { _ts?: number }
    if (typeof parsed._ts === "number" && Date.now() - parsed._ts > TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }
    const { _ts, ...rest } = parsed
    void _ts
    return rest
  } catch {
    return null
  }
}

const writeStored = (payload: AttributionPayload) => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...payload, _ts: Date.now() })
    )
  } catch {
    /* ignore quota / private-mode failures */
  }
}

/**
 * Reads attribution params from the current URL + referrer and stores
 * them when a) nothing is stored yet, or b) a fresh paid-click ID is
 * present (gclid/fbclid/etc.). Idempotent — safe to call on every mount.
 *
 * Returns the merged payload that's now in storage, or null if nothing
 * was captured/stored on this visit.
 */
export const captureAttributionFromCurrentLocation = (): AttributionPayload | null => {
  if (typeof window === "undefined") return null

  const url = new URL(window.location.href)
  const fromUrl: AttributionPayload = {}
  for (const key of PARAM_KEYS) {
    const value = url.searchParams.get(key)
    if (value) fromUrl[key] = value
  }

  const stored = readStored()

  if (Object.keys(fromUrl).length === 0) {
    return stored
  }

  // Only overwrite stored attribution when a paid-click ID arrives, or
  // when nothing was previously stored. Organic UTM tags don't displace
  // an earlier paid-click capture.
  const shouldOverwrite =
    !stored || isPaidClickIdPresent(fromUrl) || !isPaidClickIdPresent(stored)
  if (!shouldOverwrite) {
    return stored
  }

  const merged: AttributionPayload = {
    ...fromUrl,
    landing_url: window.location.href,
    referrer: document.referrer || undefined,
    captured_at: new Date().toISOString(),
  }
  writeStored(merged)
  return merged
}

/**
 * Returns the currently-stored attribution payload, if any. Used by the
 * cart-creation hook to stamp metadata on a freshly-created cart.
 */
export const getStoredAttribution = (): AttributionPayload | null => readStored()

/**
 * Clear stored attribution — call after the customer's order is placed
 * if you want to reset attribution on the next visit. Off by default;
 * the 30-day TTL handles cleanup naturally.
 */
export const clearStoredAttribution = () => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
}
